import { Component, ComponentRef, ViewContainerRef, computed, signal, inject, effect, OnDestroy, viewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DialogActions, DialogInjectableComponent, DialogData } from './types';
import { MatTabsModule } from '@angular/material/tabs';
import { TabHostDirective } from './tab-host-directive';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dialog',
  imports: [
    MatDialogModule,
    MatTabsModule,
    TabHostDirective,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './dialog.html'
})
export class Dialog implements OnDestroy {
  // Hosts are registered by key (tab name) to create components inside them
  private hosts = signal(new Map<string, ViewContainerRef>());

  // Components are cached per tab to preserve state when switching tabs
  private comps = signal(new Map<string, ComponentRef<unknown>>());

  private currentKey = signal<string>('');

  // Find all hosts in the template using the marker directive
  private hostDirectives = viewChildren(TabHostDirective);

  private dialogRef = inject(MatDialogRef<Dialog>);
  readonly data: DialogData = inject<DialogData>(MAT_DIALOG_DATA, { optional: true }) ?? { tabs: [] };

  // Sync the hosts map whenever the view updates (directives are rendered)
  private syncHosts = effect(() => {
    const list = this.hostDirectives();
    if (list.length === 0) return;

    this.hosts.update(prev => {
      const next = new Map(prev);
      list.forEach(dir => {
        const key = dir.tabKey();
        if (key && !next.has(key)) {
          next.set(key, dir.vcr);
        }
      });
      return next;
    });
  });

  // Re-render the current component whenever the key or the host changes
  private reRender = effect(() => {
    const key = this.currentKey();
    const host = this.hosts().get(key);
    if (host && key) {
      this.renderByKey(key);
    }
  });

  constructor() {
    // Initialize with first tab if available
    const firstTab = this.data.tabs[0]?.name;
    if (firstTab) {
      this.currentKey.set(firstTab);
    }
  }

  private currentTab = computed(() => this.data.tabs.find(t => t.name === this.currentKey()));

  protected currentActions = computed(() => {
    const defaults: DialogActions = {
      showSave: false,
      showSaveAndContinue: false,
      showClose: true,
      closeLabel: 'Cerrar',
      saveLabel: 'Guardar',
      saveAndContinueLabel: 'Guardar y continuar',
    };
    return { ...defaults, ...this.data.defaultActions, ...this.currentTab()?.actions };
  });

  protected hasActions = computed(() => {
    const actions = this.currentActions();
    return actions.showSave || actions.showSaveAndContinue || actions.showClose;
  });

  protected isCompactSingleTab = computed(() =>
    this.data.variant === 'compact' && this.data.tabs.length === 1
  );

  protected isSingleTabHeaderHidden = computed(() =>
    this.data.hideSingleTabHeader === true && this.data.tabs.length === 1
  );

  protected contentClass = computed(() => {
    const mode = this.data.mode ?? 'bounded';
    const variant = this.data.variant;
    const classes = ['dialog-content', `dialog-content--${mode}`];
    if (variant) classes.push(`dialog-content--${variant}`);
    return classes.join(' ');
  });

  protected canSave = computed(() => this.getCurrentComponent()?.canSave?.() ?? true);
  protected saving = computed(() => this.getCurrentComponent()?.isSaving?.() ?? false);

  onTabChange = (index: number) => {
    const nextKey = this.data.tabs[index]?.name;
    if (nextKey) this.currentKey.set(nextKey);
  };

  private renderByKey(key: string): void {
    const host = this.hosts().get(key);
    if (!host) return;

    const tab = this.data.tabs.find(t => t.name === key);
    if (!tab) return;

    let existing = this.comps().get(key);

    // If existing component was destroyed (shouldn't happen here but for safety)
    if (existing?.hostView.destroyed) {
      this.comps.update(m => {
        const next = new Map(m);
        next.delete(key);
        return next;
      });
      existing = undefined;
    }

    if (existing) {
      // Re-insert view if it's not in the host
      if (host.indexOf(existing.hostView) === -1) {
        host.insert(existing.hostView);
      }
      // Re-apply inputs
      if (tab.data) {
        Object.entries(tab.data).forEach(([k, v]) => existing.setInput(k, v));
      }
      return;
    }

    // Create NEW component instance
    if (!tab.component) {
      console.error(`[Dialog] Component for tab "${key}" is UNDEFINED.`);
      return;
    }

    try {
      const ref = host.createComponent(tab.component);
      if (tab.data) {
        Object.entries(tab.data).forEach(([k, v]) => ref.setInput(k, v));
      }
      this.comps.update(comps => {
        const next = new Map(comps);
        next.set(key, ref);
        return next;
      });
    } catch (err) {
      console.error(`[Dialog] Fallo al crear componente para tab "${key}":`, err);
    }
  }

  private getCurrentComponent = (): DialogInjectableComponent | null => {
    const key = this.currentKey();
    return key ? (this.comps().get(key)?.instance as DialogInjectableComponent || null) : null;
  };

  async onSaveAction(): Promise<void> {
    if (this.saving() || !this.canSave()) return;
    const c = this.getCurrentComponent();
    try {
      let result: unknown = undefined;
      if (c?.onSave) {
        result = await c.onSave();
      }

      if (result !== false) {
        this.dialogRef.close(result ?? true);
      }
    } catch (e) {
      console.warn('[Dialog] Save action failed or was cancelled by component', e);
    }
  }

  async onSaveAndContinueAction(): Promise<void> {
    if (this.saving() || !this.canSave()) return;
    const c = this.getCurrentComponent();
    try {
      if (c?.onSaveAndContinue) {
        await c.onSaveAndContinue();
      } else if (c?.onSave) {
        await c.onSave();
      }
    } catch (e) {
      console.warn('[Dialog] Save-and-continue action failed or was cancelled by component', e);
    }
  }

  onCloseAction(): void {
    if (this.saving()) return;
    const c = this.getCurrentComponent();
    if (c?.onCancel) c.onCancel();
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    // Clean up all cached components
    for (const ref of this.comps().values()) {
      if (!ref.hostView.destroyed) ref.destroy();
    }
  }
}
